import * as actionTypes from "../actions/actionTypes";
import {
  CLEAR_BLOCKS,
  MARK_BLOCKS_REFRESHING,
  AI_CLEAR_TOOL_RESPONSE,
} from "../actions/actionTypes";
import { format } from "date-fns";
import {
  buildLocalRowExpandGroupedData,
  doMerge,
} from "../../utils/dataOptionsValues";
import { produce } from "immer";
import { matchingArrayKeysAndObject } from "../../utils/func";
import padEmptyDateValues from "../../utils/dates/padEmptyDateValues";
import { omit } from "lodash-es";
import { exportXlsx } from "../../utils/exports/exportTableToExcel";
import { absoluteDate } from "../../utils/dates/dateFunc";
import { setDataUniqueRowUuid } from "./layoutReducer";
import { getMappedDrilldownData } from "../../Pages/Usage/helper";
import exportWithStyles from "../../utils/exports/exportWithStyles";

const initialState = {
  blocks: [],
  detailsTable: {
    loading: false,
    error: false,
  },
  message: null,
  loadBundleId: null,
  rowExpandedCharts: {},
  currentVisId: null,
  exportAllCharts: null,
  tableEditorPreviewChart: null,
  runOnQueryFiltersChecked: false,
  rowExpandedChartsHeight: {},
  lastExecSuccessTimestamp: null,
  rowExpandedChartsHiddenColumns: {},
  aiSidebar: {
    open: false,
    loading: false,
    content: null,
    error: false,
    title: null,
    visualizationId: null,
    conversationId: null,
    messages: [],
    followUpLoading: false,
    requestInFlight: false,
    streamingMessageId: null,
    streamingMessageIndex: null,
    aiResultsModalOpen: false,
    activeResultMessageId: null,
  },
  dynamicSubTitles: {},
};

export const hideCharts = (newFilters, q) => {
  const activeFilters = newFilters.filter((f) => f.checked).map((f) => f.type);
  if (q.hideOnFilters) {
    if (q.hideOnFilters.find((hof) => activeFilters.find((af) => hof === af)))
      return true;
  }

  if (q.showOnFilters) {
    if (
      q.showOnFilters.find((sof) => !activeFilters.find((af) => af === sof))
    ) {
      return true;
    }
  }

  if (!q.filters) return false;

  return newFilters.length > 0
    ? !newFilters.some((nf) => {
        const filterable = q.filters.filter((f) => f.type === nf.type);
        return filterable.length > 0
          ? filterable.some((ft) => ft.value === nf.value)
          : true;
      })
    : false;
};

export default produce((draft = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_LOAD_BUNDLE:
      const { visualizationUuid, loadBundleId } = action;
      const ids = visualizationUuid ? [].concat(visualizationUuid) : null;

      draft.blocks
        .filter((b) => !b.section)
        .filter((b) => {
          // make refresing: true, only for defined visualizationId
          // use case writebacks "reloadTableVisualizationUuid"
          if (ids) {
            return b.charts.some((c) => ids.includes(c.visualizationId));
          }
          return true;
        })
        .forEach((b) => {
          b.charts.forEach((c) => {
            if (c.loaded) {
              c.refreshing = true;
            }
          });
        });
      draft.loadBundleId = loadBundleId;
      break;

    case actionTypes.EXECUTE_QUERY_START: {
      if (action.meta?.forPreview) {
        draft.tableEditorPreviewChart = {
          ...(draft.tableEditorPreviewChart ?? {}),
          refreshing: true,
        };
      }
      break;
    }

    case actionTypes.EXECUTE_QUERY_SUCCESS: {
      if (action.slice !== "dashboard") return;
      const { data, meta } = action.results;
      const chart = action?.meta?.chart ?? action.chart;

      let tData = setDataUniqueRowUuid(data);

      if (chart?.localRowExpandGrouping) {
        const { mainData, rowExpandedCharts } = buildLocalRowExpandGroupedData(
          tData,
          chart,
          meta
        );

        tData = mainData;
        draft.rowExpandedCharts[chart.uuid] = rowExpandedCharts;
      }

      const matchParams = [
        ["blocks", "charts"],
        "visualizationId",
        action.visualizationId,
      ];

      if (action.meta?.forPreview) {
        draft.tableEditorPreviewChart = {
          ...chart,
          meta,
          data: tData,
          loading: false,
          refreshing: false,
        };
        draft.lastExecSuccessTimestamp = new Date();
        return;
      }

      const matched = matchingArrayKeysAndObject(...matchParams)(draft.blocks);

      if (!matched) {
        return;
      }

      const { chartsKey, blocksKey } = matched;
      const shouldPadZero = draft.blocks[blocksKey].charts[chartsKey].padZero;

      const formattedData = padEmptyDateValues(
        tData,
        action.dateFilters,
        shouldPadZero
      );

      const updateChart = doMerge(
        draft.blocks,
        action.visualizationId,
        formattedData,
        action.cacheKey,
        meta
      );

      draft.blocks[blocksKey].charts[chartsKey] = {
        ...matched.obj,
        ...updateChart,
      };

      draft.lastExecSuccessTimestamp = new Date();
      break;
    }

    case actionTypes.EXECUTE_QUERY_FAIL: {
      if (action.slice !== "dashboard") return;

      if (action.meta?.forPreview) {
        draft.tableEditorPreviewChart = {
          ...action.meta.chart,
          error: action.error,
          loading: false,
          refreshing: false,
        };
        return;
      }

      const matchParams = [
        ["blocks", "charts"],
        "visualizationId",
        action.visualizationId,
      ];
      const matched = matchingArrayKeysAndObject(...matchParams)(draft.blocks);
      if (!matched) return;

      const restricted =
        action.error && action.error.message.includes("restricted");

      if (action.error?.message === "canceled") {
        return;
      } else {
        draft.blocks[matched.blocksKey].charts[matched.chartsKey] = {
          ...matched.obj,
          ...(action.error && { error: action.error, restricted }),
          data: [],
          loading: false,
          refreshing: false,
        };
      }
      break;
    }

    case actionTypes.SET_CHART_VISIBILITY: {
      const newFilters = action.filters.filter((f) => f.checked);
      draft.blocks.forEach((block) => {
        block.hidden =
          block.hide ||
          (block.charts[0] && hideCharts(newFilters, block.charts[0]));
      });

      break;
    }

    case actionTypes.LOAD_DASHBOARD_CONFIGURATION_SUCCESS: {
      const { name, blocks } = action.results;
      draft.name = name;
      draft.message = null;
      draft.blocks = blocks.map((block) => ({
        ...block,
        charts: block.section
          ? []
          : block.charts.map((chart) => ({
              ...chart,
              loading: true,
              data: [],
              error: null,
              hide: !chart.availableForTerms
                ? false
                : !chart.availableForTerms.find((t) => t === action.term),
            })),
      }));
      break;
    }

    case actionTypes.LOAD_DASHBOARD_FILE_CONFIGURATION_SUCCESS: {
      const { blocks } = action.results;
      draft.fileBlocks = blocks.map((block) => ({
        ...block,
        charts: block.charts.map((chart) => ({ ...chart, data: [] })),
      }));
      break;
    }

    case actionTypes.LOAD_DASHBOARD_CONFIGURATION_FAIL: {
      draft.message = action.message;
      draft.blocks = [];
      break;
    }

    case actionTypes.SHOW_ALL_TABLE_RECORDS_START: {
      draft.detailsTable = {
        data: action.dropPreviousData ? [] : draft.detailsTable.data, // on Filters change we need to drop prev data and pagination value
        meta: draft.detailsTable.meta,
        loading: action.dropPreviousData || !draft.detailsTable.data,
        isNextPageLoading: !!draft.detailsTable?.data,
        error: false,
      };
      break;
    }

    case actionTypes.SHOW_ALL_TABLE_RECORDS_SUCCESS: {
      const { data, meta } = action.results;

      draft.detailsTable = {
        loading: false,
        isNextPageLoading: false,
        data: [
          ...(draft.detailsTable.data || []),
          ...data.map((item) =>
            item.CloseDate
              ? {
                  ...item,
                  CloseDate: format(absoluteDate(item.CloseDate), "M/dd/yyy"),
                }
              : item
          ),
        ],
        meta,
      };
      break;
    }

    case actionTypes.SHOW_ALL_TABLE_RECORDS_FAIL: {
      draft.detailsTable = {
        data: draft.detailsTable.data,
        meta: draft.detailsTable.meta,
        isNextPageLoading: false,
        loading: false,
        error: true,
      };
      break;
    }

    case actionTypes.CLEAR_ALL_TABLE_RECORDS: {
      draft.detailsTable = { loading: false };
      break;
    }

    case actionTypes.DOWNLOAD_TABLE_DATA_START: {
      draft.downloading = {
        loading: true,
        error: null,
      };
      break;
    }

    case actionTypes.DOWNLOAD_TABLE_DATA_SUCCESS: {
      draft.downloading = {
        loading: false,
        error: null,
      };
      break;
    }

    case actionTypes.DOWNLOAD_TABLE_DATA_FAIL: {
      draft.downloading = {
        loading: false,
        error: action.error,
      };
      break;
    }

    case actionTypes.DOWNLOAD_TABLE_DATA_RESET: {
      delete draft.downloading;
      break;
    }

    case actionTypes.STOP_DASHBOARD_SYNC: {
      draft.loadBundleId = null;
      break;
    }

    case actionTypes.SET_DATE_TERM: {
      draft.blocks.forEach((block) => {
        block.charts.forEach((chart) => {
          chart.data = [];
          if (chart.availableForTerms) {
            chart.hide = !chart.availableForTerms.find(
              (t) => t === action.term
            );
          }
        });
      });
      break;
    }

    case actionTypes.ROW_EXPAND_EXEC_QUERY_START: {
      draft.rowExpandedCharts[action.visualizationId] = {
        ...action.chart,
        loading: !action.chart.loaded,
        refreshing: action.chart.loaded,
        filters: action.filters,
      };
      break;
    }

    case actionTypes.ROW_EXPAND_EXEC_QUERY_EXPORT_ALL_START: {
      draft.exportAllCharts = {
        ...action.chart,
        loading: !action.chart.loaded,
        refreshing: action.chart.loaded,
        meta: [],
        filters: action.filters,
      };
      break;
    }

    case actionTypes.ROW_EXPAND_EXEC_QUERY_SUCCESS: {
      const { usage } = action.chart;
      const unifiedData = setDataUniqueRowUuid(action.results.data);

      const data = usage ? getMappedDrilldownData(unifiedData) : unifiedData;

      draft.rowExpandedCharts[action.visualizationId] = {
        ...action.chart,
        data,
        additionalData: action.results.data,
        loading: false,
        meta: action.results.meta,
        refreshing: false,
        loaded: true,
        filters: action.filters,
      };
      break;
    }

    case actionTypes.ROW_EXPAND_EXEC_QUERY_EXPORT_ALL_SUCCESS: {
      draft.exportAllCharts = {
        ...action.chart,
        data: action.results.data,
        loading: false,
        meta: action.results.meta,
        refreshing: false,
        loaded: true,
        filters: action.filters,
      };
      break;
    }

    case actionTypes.ROW_EXPAND_EXEC_QUERY_FAIL: {
      draft.rowExpandedCharts[action.visualizationId] = {
        ...action.chart,
        data: [],
        loading: false,
        ...(action.error && { error: action.error.message }),
        refreshing: false,
        loaded: true,
      };
      break;
    }

    case actionTypes.ROW_EXPAND_EXEC_QUERY_EXPORT_ALL_FAIL: {
      draft.exportAllCharts = {
        ...action.chart,
        data: [],
        loading: false,
        ...(action.error && { error: action.error.message }),
        refreshing: false,
        loaded: true,
      };
      break;
    }

    case actionTypes.CLEAR_EXPORT_ALL_OBJECT: {
      draft.exportAllCharts = null;
      break;
    }

    case actionTypes.HANDLE_TABLE_PAGINATION:
      draft.blocks.forEach((b) => {
        b.charts.forEach((c) => {
          if (c.loaded && action.chart.visualizationId === c.visualizationId) {
            c.refreshing = true;
          }
        });
      });
      break;

    case actionTypes.REMOVE_DEEPER_EXPANDED_ROWS:
      const { keys, tableId, nonConvertPercentsBack, title } = action;

      if (keys) {
        draft.rowExpandedCharts = omit(draft.rowExpandedCharts, keys);
      }
      draft.currentVisId = tableId;
      draft.nonConvertPercentsBack = !nonConvertPercentsBack;

      // you need to wait until the extended row closes
      setTimeout(() => {
        if (action.withStyles) {
          return exportWithStyles(
            tableId,
            title,
            action.page,
            action.dateFilters,
            action.menuFilters,
            action.domain
          );
        }
        exportXlsx(action);
      }, 500);

      break;

    case actionTypes.UPDATE_VISUALIZATION_SUCCESS:
      draft.blocks = draft.blocks.map((block) => {
        if (block.uuid === action.blockUuid) {
          return {
            ...block,
            charts: block.charts.map((chart) => ({
              ...chart,
              ...action.results.data.settings,
            })),
          };
        }
        return block;
      });
      break;

    case CLEAR_BLOCKS: {
      draft.blocks = [];
      break;
    }

    case MARK_BLOCKS_REFRESHING: {
      for (const block of draft.blocks) {
        for (const chart of block.charts) {
          chart.refreshing = true;
        }
      }
      break;
    }

    case actionTypes.SET_LOCAL_DRILLDOWNS_DATA: {
      draft.rowExpandedCharts = action.rowExpandedCharts;
      break;
    }

    case actionTypes.REFRESH_POWER_EDITOR_PREVIEW:
      draft.tableEditorPreviewChart = null;
      break;

    case actionTypes.RUN_ON_QUERY_FILTERS_CHECKED:
      draft.runOnQueryFiltersChecked = action.checked;
      break;

    case actionTypes.SET_ROW_EXPANDED_CHARTS_HEIGHT:
      draft.rowExpandedChartsHeight[action.index] = action.height;
      break;

    case actionTypes.SAVE_EXPANDED_ROW_HIDDEN_COLUMNS: {
      const { visualizationId, keys, arrays } = action;
      // use same level object key name for drilldown level
      // no individual levels for this (https://treehousetechgroup.atlassian.net/browse/IO2-2790)
      const [, restVis] = String(visualizationId).split("row");
      draft.rowExpandedChartsHiddenColumns = {
        ...draft.rowExpandedChartsHiddenColumns,
        [restVis]: Object.fromEntries(
          keys.map((key, index) => [key, arrays[index]])
        ),
      };
      break;
    }

    case actionTypes.AI_ANALYZE_VISUALIZATION_START:
      draft.aiSidebar.open = true;
      draft.aiSidebar.loading = true;
      draft.aiSidebar.requestInFlight = true;
      draft.aiSidebar.content = null;
      draft.aiSidebar.toolResponse = null;
      draft.aiSidebar.error = null;
      draft.aiSidebar.title = null;
      draft.aiSidebar.visualizationId = null;
      draft.aiSidebar.conversationId = null;
      draft.aiSidebar.messages = [];
      draft.aiSidebar.initialQuestion = action.payload?.question;
      draft.aiSidebar.activeResultMessageId = null;
      break;

    case actionTypes.AI_ANALYZE_VISUALIZATION_SUCCESS:
      draft.aiSidebar.loading = false;
      draft.aiSidebar.requestInFlight = false;
      draft.aiSidebar.content = action.description;
      draft.aiSidebar.error = null;
      draft.aiSidebar.title = action.title;
      draft.aiSidebar.visualizationId = action.visualizationId;
      draft.aiSidebar.conversationId = action.conversationId;
      break;

    case actionTypes.AI_ANALYZE_VISUALIZATION_FAIL:
      draft.aiSidebar.loading = false;
      draft.aiSidebar.requestInFlight = false;
      draft.aiSidebar.content = null;
      draft.aiSidebar.title = null;
      draft.aiSidebar.visualizationId = null;
      draft.aiSidebar.error = action.error;
      draft.aiSidebar.conversationId = null;
      break;

    case actionTypes.AI_ANALYZE_VISUALIZATION_PROGRESS:
      draft.aiSidebar.loading = false;
      draft.aiSidebar.requestInFlight = true;
      draft.aiSidebar.content = action.description;
      draft.aiSidebar.error = null;
      draft.aiSidebar.title = action.title;
      draft.aiSidebar.conversationId = action.conversationId;
      break;

    case actionTypes.AI_TOGGLE_SIDEBAR:
      draft.aiSidebar.open = action.state;
      break;

    case actionTypes.AI_HIDE_SIDEBAR:
      draft.aiSidebar.open = false;
      break;

    case actionTypes.AI_CLOSE_SIDEBAR:
      draft.aiSidebar.open = false;
      draft.aiSidebar.loading = false;
      draft.aiSidebar.content = null;
      draft.aiSidebar.title = null;
      draft.aiSidebar.visualizationId = null;
      draft.aiSidebar.conversationId = null;
      draft.aiSidebar.messages = [];
      draft.aiSidebar.followUpLoading = false;
      draft.aiSidebar.aiResultsModalOpen = false;
      break;

    case actionTypes.AI_RESULTS_MODAL_OPEN:
      draft.aiSidebar.aiResultsModalOpen = action.open;
      break;

    case actionTypes.AI_RESTORE_CONVERSATION:
      draft.aiSidebar.conversationId = action.conversationId;
      draft.aiSidebar.messages = action.messages;
      draft.aiSidebar.toolResponse = action.toolResponse;
      draft.aiSidebar.title = action.title;
      draft.aiSidebar.aiResultsModalOpen = true;
      draft.aiSidebar.loading = false;
      draft.aiSidebar.followUpLoading = false;
      draft.aiSidebar.error = null;
      draft.aiSidebar.streamingMessageId = null;
      draft.aiSidebar.streamingMessageIndex = null;
      draft.aiSidebar.activeResultMessageId = null;
      break;

    case actionTypes.AI_FOLLOW_UP_START:
      if (!draft.aiSidebar.aiResultsModalOpen) {
        draft.aiSidebar.open = true;
      }
      draft.aiSidebar.followUpLoading = true;
      draft.aiSidebar.streamingMessageId = null;
      draft.aiSidebar.streamingMessageIndex = null;
      draft.aiSidebar.activeResultMessageId = null;
      draft.aiSidebar.messages.push({
        type: "user",
        content: action.question,
        timestamp: new Date().toISOString(),
      });
      // Create placeholder AI message to stream into
      draft.aiSidebar.messages.push({
        type: "ai",
        id: null,
        content: null,
        steps: [],
        timestamp: new Date().toISOString(),
      });
      draft.aiSidebar.streamingMessageIndex =
        draft.aiSidebar.messages.length - 1;
      break;

    case actionTypes.AI_FOLLOW_UP_SUCCESS:
      draft.aiSidebar.followUpLoading = false;
      draft.aiSidebar.messages.push({
        type: "ai",
        content: action.answer,
        steps: [],
        timestamp: new Date().toISOString(),
      });
      break;

    case actionTypes.AI_FOLLOW_UP_PROGRESS: {
      // Route all streaming events to the placeholder AI message created at START
      const idx = draft.aiSidebar.streamingMessageIndex;
      let targetMessage = idx != null ? draft.aiSidebar.messages[idx] : null;

      // If a message_id arrives (either as eventType or via message_created), remember it
      if (action.eventType === "message_id" && action.answer) {
        if (targetMessage) {
          targetMessage.id = action.answer;
        }
        draft.aiSidebar.streamingMessageId = action.answer;
      } else if (action.eventType === "message_created" && action.messageId) {
        // message_created event carries messageId in the payload
        draft.aiSidebar.streamingMessageId = action.messageId;
        if (targetMessage) {
          targetMessage.id = action.messageId;
        }
      }

      // If for some reason we don't have the placeholder, try to resolve by ID
      if (!targetMessage) {
        const activeMessageId =
          action.messageId || draft.aiSidebar.streamingMessageId;
        if (activeMessageId) {
          targetMessage = draft.aiSidebar.messages.find(
            (m) => m.id === activeMessageId
          );
        }
      }

      // If still missing, create a new AI message and set it as the streaming target
      if (!targetMessage) {
        draft.aiSidebar.messages.push({
          type: "ai",
          id: action.messageId || draft.aiSidebar.streamingMessageId || null,
          content: null,
          steps: [],
          timestamp: new Date().toISOString(),
        });
        draft.aiSidebar.streamingMessageIndex =
          draft.aiSidebar.messages.length - 1;
        targetMessage =
          draft.aiSidebar.messages[draft.aiSidebar.streamingMessageIndex];
      }

      // Apply updates
      if (action.eventType === "content") {
        const isFirstContent =
          !targetMessage.content || targetMessage.content.length === 0;
        targetMessage.content = (targetMessage.content || "") + action.answer;
        if (isFirstContent) {
          if (!Array.isArray(targetMessage.steps)) targetMessage.steps = [];
          const lastThinkingStep = [...(targetMessage.steps || [])]
            .reverse()
            .find((s) => s.type === "thinking");
          if (lastThinkingStep && !lastThinkingStep.completedAt) {
            lastThinkingStep.completedAt = new Date().toISOString();
          }
          targetMessage.steps.push({
            type: "content_start",
            content: "",
            timestamp: new Date().toISOString(),
          });
        }
        draft.aiSidebar.followUpLoading = true; // remain loading until COMPLETE
      } else if (action.eventType === "thinking") {
        if (!Array.isArray(targetMessage.steps)) targetMessage.steps = [];
        const lastStep = targetMessage.steps[targetMessage.steps.length - 1];
        if (lastStep && lastStep.type === "thinking") {
          lastStep.content = (lastStep.content || "") + action.answer;
          lastStep.timestamp = new Date().toISOString();
        } else {
          targetMessage.steps.push({
            type: "thinking",
            content: action.answer,
            timestamp: new Date().toISOString(),
          });
        }
      } else if (action.eventType === "tool") {
        const toolResponse = JSON.parse(action.answer);
        if (toolResponse.data?.length) {
          draft.aiSidebar.toolResponse = toolResponse;
        }
        // Add tool result as a step so we can mark the preceding tool_use as complete
        if (!Array.isArray(targetMessage.steps)) targetMessage.steps = [];
        targetMessage.steps.push({
          type: "tool",
          content: action.answer,
          timestamp: new Date().toISOString(),
        });
      } else if (action.eventType === "conversation_id") {
        draft.aiSidebar.conversationId = action.answer;
      } else if (action.eventType === "title_updated") {
        draft.aiSidebar.title = action.answer;
      } else if (
        action.eventType &&
        action.eventType !== "message_created" &&
        action.eventType !== "message_id"
      ) {
        if (!Array.isArray(targetMessage.steps)) targetMessage.steps = [];
        // Mark last thinking step as completed when tool_use (or other step) starts
        const lastThinkingStep = [...targetMessage.steps]
          .reverse()
          .find((s) => s.type === "thinking");
        if (lastThinkingStep && !lastThinkingStep.completedAt) {
          lastThinkingStep.completedAt = new Date().toISOString();
        }

        // need to keep order of steps
        targetMessage.content =
          (targetMessage.content ?? "") + "<::>" + action.answer + "<::>";

        targetMessage.steps.push({
          type: action.eventType,
          content: action.answer,
          timestamp: new Date().toISOString(),
        });
      }

      targetMessage.timestamp = new Date().toISOString();
      break;
    }

    case actionTypes.AI_FOLLOW_UP_COMPLETE: {
      draft.aiSidebar.followUpLoading = false;
      // Remove empty AI placeholder if aborted before any content arrived
      const completeIdx = draft.aiSidebar.streamingMessageIndex;
      if (completeIdx != null) {
        const msg = draft.aiSidebar.messages[completeIdx];
        if (msg?.type === "ai" && !msg.content) {
          draft.aiSidebar.messages.splice(completeIdx, 1);
        }
      }
      draft.aiSidebar.streamingMessageId = null;
      draft.aiSidebar.streamingMessageIndex = null;
      draft.aiSidebar.error = null;
      break;
    }

    case actionTypes.AI_FOLLOW_UP_FAIL: {
      draft.aiSidebar.followUpLoading = false;
      const failIdx = draft.aiSidebar.streamingMessageIndex;
      if (failIdx != null) {
        const msg = draft.aiSidebar.messages[failIdx];
        if (msg?.type === "ai" && !msg.content) {
          draft.aiSidebar.messages.splice(failIdx, 1);
        }
      }
      draft.aiSidebar.streamingMessageId = null;
      draft.aiSidebar.streamingMessageIndex = null;
      draft.aiSidebar.error = action.error;
      break;
    }

    case actionTypes.AI_RESET_ERROR:
      draft.aiSidebar.error = null;
      break;

    case AI_CLEAR_TOOL_RESPONSE: {
      draft.aiSidebar.toolResponse = null;
      draft.aiSidebar.activeResultMessageId = null;
      break;
    }

    case actionTypes.AI_SET_ACTIVE_RESULT_MESSAGE_ID: {
      draft.aiSidebar.activeResultMessageId = action.messageId;
      break;
    }

    case actionTypes.AI_MESSAGE_FEEDBACK_SUCCESS: {
      const message = draft.aiSidebar.messages.find(
        (m) => m.id === action.messageId
      );
      if (message) {
        message.feedback = action.feedback;
      }
      break;
    }

    // on tab switch remove all openeded drilldowns
    case actionTypes.CLEAR_ALL_ROW_EXPANDED_CHARTS: {
      if (action.key) {
        Object.keys(draft.rowExpandedCharts).forEach((key) => {
          if (key.includes(action.key)) {
            delete draft.rowExpandedCharts[key];
          }
        });
        break;
      }

      draft.rowExpandedCharts = {};
      draft.rowExpandedChartsHeight = {};
      break;
    }

    case actionTypes.SET_DYNAMIC_SUBTITLES:
      draft.dynamicSubTitles[action.visualizationId] = action.columnKeys;
      break;

    default:
      return draft;
  }
});
